import { apiGet, apiPatch, apiPost } from '@forwardflow/services/Axios/configs'
import {
   Calendar,
   IAppointmentPreference,
   IUser,
   IUserAppointmentPreference,
   IUserCalenderInfo,
   IUserInvite,
   IUserRole,
   ResponseSuccess,
} from '@forwardflow/types'

export class UserService {
   // Get user data
   static async getUserData() {
      const data = await apiGet<ResponseSuccess<IUser>>({
         apiPath: `/users/me`,
      })

      return data.data
   }

   // Get user by email
   static async getUserDetailsByEmail(email: string) {
      const data = await apiGet<ResponseSuccess<IUserAppointmentPreference>>({
         apiPath: `/users/email/${email}`,
      })
      return data.data
   }

   // Get user by id
   static async getUserDetailsById(userId: string) {
      const data = await apiGet<ResponseSuccess<IUserAppointmentPreference>>({
         apiPath: `/users/${userId}`,
      })
      return data.data
   }

   // Set user access role
   static async setUserAccessRole(orgId: string, userId: string, roleNames: string[]) {
      const data = await apiPost<ResponseSuccess<IUserRole>>({
         apiPath: `/organization-users/${orgId}/users/${userId}/roles`,
         data: {
            roleNames,
         },
         includeXOrg: false,
      })
      return data.data
   }

   // Send user invitation mail
   static userSendInvitation = async (
      emails: string[],
      role: string = 'Admin',
      linkedEmails?: Record<string, string>
   ) => {
      const data = await apiPost<ResponseSuccess<IUserInvite>>({
         apiPath: `/users/send-invitations`,
         data: {
            emails,
            role,
            ...(linkedEmails && Object.keys(linkedEmails).length > 0 && { linkedEmails }),
         },
      })
      return data.data
   }

   // Get user role in organization
   static getUserRoleInOrganization = async (orgId: string) => {
      const data = await apiGet<ResponseSuccess<IUserRole>>({
         apiPath: `/users/org-roles?userOrgId=${orgId}`,
         includeXOrg: false,
      })
      return data.data
   }

   static getAgentOnboardingStatus = async () => {
      const data = await apiGet<ResponseSuccess<{ agentOnboardingCompleted: boolean | null }>>({
         apiPath: `/users/agent-onboarding-status`,
      })
      return data.data
   }

   static completeAgentOnboarding = async (email: string) => {
      const data = await apiPatch<ResponseSuccess<{ message: string }>>({
         apiPath: `/users/user-info`,
         data: { email, agentOnboardingCompleted: true },
      })
      return data.data
   }

   // Accept organization invitation
   static acceptOrganizationInvitation = async (invitationId: string) => {
      const data = await apiPost<ResponseSuccess<IUserInvite>>({
         apiPath: `/users/accept-invitation`,
         data: {
            orgId: invitationId,
         },
         includeXOrg: false,
      })
      return data.data
   }

   // Reject organization invitation
   static rejectOrganizationInvitation = async (invitationId: string) => {
      const data = await apiPost<ResponseSuccess<IUserInvite>>({
         apiPath: `/users/reject-invitation`,
         data: {
            orgId: invitationId,
         },
         includeXOrg: false,
      })
      return data.data
   }

   //update first sign in tracker
   static updateFirstSignIn = async (firstSignIn: boolean) => {
      const data = await apiPatch<ResponseSuccess<any>>({
         apiPath: `/users/first-sign-in`,
         data: {
            firstSignIn: firstSignIn,
         },
         includeXOrg: false,
      })
      return data.data
   }

   static sendCard = async () => {
      const data = await apiPost<ResponseSuccess<{ message: string }>>({
         apiPath: `/users/send-card`,
      })
      return data.data
   }

   // Get User Calender Info
   static getUserCalenderInfo = async () => {
      const data = await apiGet<ResponseSuccess<IUserCalenderInfo>>({
         apiPath: `/users/calendar-info`,
         includeXOrg: true,
      })
      return data.data
   }

   // Connect User with Calender
   static getConnectUserWithCalender = async (redirectUrl: string, orgId: string) => {
      const data = await apiGet<ResponseSuccess<any>>({
         apiPath: `/users/calendar-connect?redirectUrl=${redirectUrl}&orgIdentifier=${orgId}`,
         includeXOrg: false,
      })
      return data.data
   }

   // Connect User with Outlook Calendar
   static getConnectUserWithOutlookCalendar = async (
      redirectUrl: string,
      orgId: string
   ) => {
      const data = await apiGet<ResponseSuccess<any>>({
         apiPath: `/users/outlook-calendar-connect?redirectUrl=${redirectUrl}&orgIdentifier=${orgId}`,
         includeXOrg: false,
      })
      return data.data
   }

   // update appointment preference
   static updateAppointmentPreference = async (
      userPreference: IAppointmentPreference
   ) => {
      const data = await apiPatch<ResponseSuccess<any>>({
         apiPath: `/users/update-appointment-data?`,
         data: {
            emails: [userPreference.email],
            appointmentData: {
               meetingPlatform:
                  userPreference?.selectedPreference?.toLocaleLowerCase() ===
                  'phone-call'
                     ? 'phone-call'
                     : userPreference?.virtualData?.platform.toLowerCase(),
               appointmentType:
                  userPreference?.selectedPreference?.toLocaleLowerCase() ===
                  'phone-call'
                     ? 'virtual'
                     : userPreference?.selectedPreference?.toLocaleLowerCase(),
               meetingLink: userPreference.virtualData.url,
               meetingId: userPreference.virtualData.meetingId,
               meetingPasscode: userPreference.virtualData.password,
               meetingAddress: {
                  address: userPreference.addressData.addressLine1,
                  street: userPreference.addressData.street,
                  city: userPreference.addressData.city,
                  state: userPreference.addressData.state,
               },
            },
         },
      })
      return data.data
   }

   static getCalendarList = async () => {
      const data = await apiGet<ResponseSuccess<any>>({
         apiPath: `/users/available-calendars`,
         includeXOrg: true,
      })
      return data.data
   }

   static setPreferredCalendarList = async (
      userEmail: string,
      preferredCalendars: Calendar[]
   ) => {
      const data = await apiPatch<ResponseSuccess<any>>({
         apiPath: `/users/preferred-calendar-list`,
         data: {
            userEmail,
            preferredCalendars,
         },
         includeXOrg: true,
      })
      return data.data
   }
}
